import controlRegisterJson from './content/control-register.json';
import controlQuestionsJson from './content/control-questions.json';
import systemDescriptionJson from './content/system-description-template.json';
import policyTemplatesJson from './content/policy-templates.json';
import onboardingJson from './content/onboarding-template.json';
import preworkJson from './content/prework-checklist.json';
import kickoffJson from './content/kickoff.json';

import type {
  AssessedControl,
  Cadence,
  Control,
  ControlRegister,
  ControlStatus,
  Kickoff,
  OnboardingField,
  OnboardingTemplate,
  PolicyTemplates,
  PreworkItem,
  StakeholderRole,
  SystemDescriptionTemplate,
  TrustFamily,
} from './schema';

export * from './schema';

export const controlRegister = controlRegisterJson as unknown as ControlRegister;

/** Plain-language Phase 2 questionnaire copy, keyed by control code. */
export const controlQuestions = controlQuestionsJson as Record<
  string,
  { question: string; helper: string; example?: string }
>;

/** Register controls enriched with their friendly question, helper, + example. */
export const controls = (controlRegister.controls as unknown as Control[]).map(
  (c) => {
    const q = controlQuestions[c.code];
    return q
      ? { ...c, question: q.question, helper: q.helper, example: q.example }
      : c;
  },
);
export const roleTaxonomy = controlRegister.roleTaxonomy as Record<StakeholderRole, string>;
export const systemDescriptionTemplate =
  systemDescriptionJson as unknown as SystemDescriptionTemplate;
export const policyTemplates = policyTemplatesJson as unknown as PolicyTemplates;
export const onboardingTemplate = onboardingJson as unknown as OnboardingTemplate;
export const preworkChecklist = preworkJson as unknown as PreworkItem[];
export const kickoff = kickoffJson as unknown as Kickoff;

/** The default demo company name used across the kit until a profile is set. */
export const DEMO_COMPANY = 'Acme Cloud Inc.';

/** Short role labels for compact UI (chips, table cells). */
export const roleShortLabels: Record<StakeholderRole, string> = {
  security: 'Security',
  engineering: 'Engineering',
  it: 'IT',
  hr: 'People/HR',
  legal: 'Legal/Privacy',
  risk: 'Risk',
  management: 'Management',
};

export const trustFamilyLabels: Record<TrustFamily, string> = {
  CC: 'Security',
  A: 'Availability',
  C: 'Confidentiality',
  P: 'Privacy',
  PI: 'Processing Integrity',
};

const STATUS_CYCLE: ControlStatus[] = ['met', 'partial', 'gap', 'na'];
const CADENCE_CYCLE: Cadence[] = [
  'Monthly',
  'Quarterly',
  'Annual',
  'Per-event',
  'One-time',
];

/** Deterministic hash so the demo state is stable across reloads. */
function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

/**
 * Seed a demo gap-analysis state onto the register. The real product will read
 * these from the engagement; here we derive a stable spread so the dashboard,
 * gap table, and evidence list look alive.
 */
export function seedAssessedControls(source: Control[] = controls): AssessedControl[] {
  return source.map((c) => {
    const h = hashCode(c.code);
    return {
      ...c,
      // Indices are always in range (modulo of a non-empty cycle); assert
      // non-null so the kit typechecks under consumers' strict index settings.
      status: STATUS_CYCLE[h % STATUS_CYCLE.length]!,
      cadence: CADENCE_CYCLE[(h >> 3) % CADENCE_CYCLE.length]!,
    };
  });
}

/** Readiness percentage: met counts full, partial half, of all non-N/A controls. */
export function readinessPct(assessed: AssessedControl[]): number {
  const scored = assessed.filter((c) => c.status !== 'na');
  if (!scored.length) return 0;
  const pts = scored.reduce(
    (a, c) => a + (c.status === 'met' ? 1 : c.status === 'partial' ? 0.5 : 0),
    0,
  );
  return Math.round((pts / scored.length) * 100);
}

/** Replace the `%s` company token in any content string. */
export function fillCompany(text: string, company = DEMO_COMPANY): string {
  return (text || '').replace(/%s/g, company);
}

/* ----- Phase 1 onboarding completion ----- */

/** Function owners, in the order shown in the RACI and kickoff. */
export const STAKEHOLDER_ORDER: StakeholderRole[] = [
  'management',
  'security',
  'engineering',
  'it',
  'legal',
  'risk',
  'hr',
];

/** Onboarding answers: a flat map. RACI owners are keyed `raci.<role>`. */
export type OnboardingAnswers = Record<string, string | string[]>;

/** Flattened onboarding fields across every group. */
export const onboardingFields: OnboardingField[] = onboardingTemplate.groups.flatMap(
  (g) => g.fields,
);
export const requiredOnboardingFields = onboardingFields.filter((f) => f.required);

/** Whether a single onboarding field has a usable answer. */
export function isOnboardingFieldFilled(
  field: OnboardingField,
  answers: OnboardingAnswers,
): boolean {
  if (field.type === 'raci') {
    return STAKEHOLDER_ORDER.every((r) =>
      ((answers[`${field.id}.${r}`] as string) || '').trim(),
    );
  }
  const v = answers[field.id];
  if (Array.isArray(v)) return v.some((x) => (x || '').trim());
  return !!(v && (v as string).trim());
}

/** Progress over the required onboarding fields. */
export function onboardingProgress(answers: OnboardingAnswers): {
  filled: number;
  total: number;
  pct: number;
} {
  const total = requiredOnboardingFields.length;
  const filled = requiredOnboardingFields.filter((f) =>
    isOnboardingFieldFilled(f, answers),
  ).length;
  return { filled, total, pct: total ? Math.round((filled / total) * 100) : 0 };
}

/** The onboarding form is complete when every required field is answered. */
export function onboardingComplete(answers: OnboardingAnswers): boolean {
  return requiredOnboardingFields.every((f) => isOnboardingFieldFilled(f, answers));
}
